import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUser, FiMenu, FiX, FiSun, FiMoon, FiLogOut, FiSettings } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout, isAuthenticated } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const navLinks = isAuthenticated ? [
    { path: '/dashboard', label: 'Dashboard', icon: FiCalendar },
    { path: '/calendar/new', label: 'Neuer Kalender', icon: FiCalendar },
    { path: '/poll/new', label: 'Neue Umfrage', icon: FiSettings },
  ] : [
    { path: '/login', label: 'Anmelden', icon: FiUser },
    { path: '/register', label: 'Registrieren', icon: FiUser },
  ];

  return (
    <header className="shadow-lg border-b border-gray-200" style={{ backgroundColor: '#2b6777' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="flex items-center">
              <img 
                src="https://letcheck.b-cdn.net/944/bild-logo-final-dunkel-1726774068395.png" 
                alt="Terminfixer Logo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold ml-3 header-text font-rubik">
                Terminfixer
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors font-rubik ${
                  isActive(link.path)
                    ? 'bg-primary-600 text-white'
                    : 'text-white hover:bg-primary-600 hover:text-white'
                }`}
              >
                <SafeIcon icon={link.icon} className="w-4 h-4" />
                <span>{link.label}</span>
              </Link>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg bg-primary-600 hover:bg-primary-700 transition-colors"
              aria-label="Theme umschalten"
            >
              <SafeIcon 
                icon={isDark ? FiSun : FiMoon} 
                className="w-5 h-5 text-white" 
              />
            </button>

            {/* User Menu (Desktop) */}
            {isAuthenticated && (
              <div className="hidden md:flex items-center space-x-4">
                <span className="text-sm text-white font-rubik">
                  Hallo, {user?.name}
                </span>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-white hover:bg-primary-600 transition-colors font-rubik"
                >
                  <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                  <span>Abmelden</span>
                </button>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 rounded-lg bg-primary-600 hover:bg-primary-700 transition-colors"
              aria-label="Menü öffnen"
            >
              <SafeIcon 
                icon={isMenuOpen ? FiX : FiMenu} 
                className="w-5 h-5 text-white" 
              />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden border-t border-primary-600"
            style={{ backgroundColor: '#2b6777' }}
          >
            <div className="px-4 py-4 space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium transition-colors font-rubik ${
                    isActive(link.path)
                      ? 'bg-primary-600 text-white'
                      : 'text-white hover:bg-primary-600'
                  }`}
                >
                  <SafeIcon icon={link.icon} className="w-5 h-5" />
                  <span>{link.label}</span>
                </Link>
              ))}

              {isAuthenticated && (
                <>
                  <div className="border-t border-primary-600 my-2"></div>
                  <div className="px-3 py-2 text-sm text-white font-rubik">
                    Angemeldet als {user?.name}
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 px-3 py-3 rounded-md text-sm font-medium text-white hover:bg-primary-600 transition-colors w-full text-left font-rubik"
                  >
                    <SafeIcon icon={FiLogOut} className="w-5 h-5" />
                    <span>Abmelden</span>
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;